// web/src/components/lifecycle/WipeConfirmation.tsx
//
// Typed-confirmation gate. The destructive button stays disabled
// until the input matches `expectedText` exactly (case-sensitive,
// trimmed). Makes it hard to wipe a location by muscle-memory
// click-through. Used by `MasterUnlockScreen`'s "Wipe app" flow,
// and by the post-unlock add-vault wizards (PR 4b) for the
// "wipe & create new" branch.

import { useState } from "react";
import { cn } from "../../lib/utils";

interface WipeConfirmationProps {
  /**
   * Exact string the user must type to arm the destructive
   * button. For S3 this is `bucket/prefix` (or just `bucket` when
   * the prefix is empty). For OPFS this is the literal `"OPFS"`.
   */
  expectedText: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export function WipeConfirmation({
  expectedText,
  onConfirm,
  onCancel,
}: WipeConfirmationProps) {
  const [input, setInput] = useState("");
  const armed = input.trim() === expectedText;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (armed) onConfirm();
      }}
    >
      <h1 className="mb-2 text-2xl font-medium tracking-tight">
        Erase everything at this location?
      </h1>
      <p className="mb-4 text-sm leading-relaxed text-text-soft">
        This deletes every file under{" "}
        <code className="rounded bg-white/5 px-1.5 py-0.5 text-[12px] text-text">
          {expectedText}
        </code>
        . The existing vault — and any other data stored at this
        location — will be unrecoverable afterwards.
      </p>

      <div className="rounded-lg border border-danger/30 bg-danger/10 p-4">
        <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-danger">
          <span className="h-1.5 w-1.5 rounded-full bg-danger" />
          Type to confirm
        </div>
        <p className="mb-3 text-[13px] leading-relaxed">
          To continue, type exactly:{" "}
          <code className="rounded bg-white/5 px-1.5 py-0.5 text-[12px] text-text">
            {expectedText}
          </code>
        </p>
        <input
          autoFocus
          value={input}
          onChange={(e) => setInput(e.target.value)}
          aria-label="Type the location to confirm wipe"
          className="w-full rounded-md border border-border bg-white/5 px-3 py-2.5 text-base text-text placeholder:text-text-dim md:py-2 md:text-sm"
          placeholder={expectedText}
          autoComplete="off"
          spellCheck={false}
        />
        <div
          className={cn(
            "mt-1.5 text-[11px]",
            armed ? "text-ok" : "text-text-dim",
          )}
        >
          {armed ? "✓ matches" : " "}
        </div>
      </div>

      <div className="mt-6 flex flex-col-reverse gap-2 md:flex-row md:justify-end">
        <button
          type="button"
          onClick={onCancel}
          className="w-full rounded-md border border-border px-4 py-3 text-sm text-text-soft md:w-auto md:py-2"
        >
          ← Back
        </button>
        <button
          type="submit"
          disabled={!armed}
          className="w-full rounded-md bg-danger px-4 py-3 text-sm font-medium text-white disabled:opacity-40 md:w-auto md:py-2"
        >
          Continue →
        </button>
      </div>
    </form>
  );
}
